import React, { useState, useMemo, useEffect } from "react";
import { PDFDocument, StandardFonts } from "pdf-lib";
import { useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Ring } from "@uiball/loaders";
import { toast } from "react-toastify";
import { createLot, getMyProduct, getStoreInfoById } from "@/utils/services/user.service";
import {
  LayoutWrapper,
  MainArea,
  Sidebar,
  Overlay,
  SlidePanel,
  SlideHeader,
  HeaderBanner,
  MainTitle,
  Subtitle,
  Card,
  CardHeader,
  SectionTitle,
  Label,
  TextInput,
  ChipButton,
  DropZone,
  HiddenFileInput,
  HierarchyButton,
  PreviewSection,
  SectionHeader,
  DataRow,
  ProgressWrapper,
  ProgressFill,
  ProductSelectWrapper,
  ProductSelectDisplay,
  ProductSelectLeft,
  ProductThumb,
  ProductSelectName,
  ProductSelectDropdown,
  ProductOption,
  LotLoadingOverlay,
} from "./PharmaLotForm.styles";

// Custodiy Pharma — Master Lot Creation (Clean Architecture)
// Main Sidebar = Data Preview
// Slide Sidebar = Supply Chain Hierarchy (callable)

export default function PharmaLotForm() {
  const { storeId } = useParams();
  const history = useHistory();
  const [storeProducts, setStoreProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [storeName, setStoreName] = useState("");

  const [sections, setSections] = useState({
    general: true,
    production: false,
    compliance: false,
    documents: false,
  });

  const [showHierarchy, setShowHierarchy] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [activeField, setActiveField] = useState(null);

  const [form, setForm] = useState({
    lotNumber: "",
    productName: "",
    productCategory: "",
    productCategoryId: "",
    gtin: "",
    manufacturer: "",
    productionDate: "",
    expiryDate: "",
    quantity: "",
    price: "",
    temperatureMin: "",
    temperatureMax: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);

  // Fetch store data to get store name
  useEffect(() => {
    if (!storeId) return;
    const fetchStoreData = async () => {
      try {
        const response = await getStoreInfoById({ store_id: storeId });
        if (response.data?.result?.[0]) {
          const store = response.data.result[0];
          const name = store.storeName || store.store_name || "";
          setStoreName(name);
          // Set manufacturer field to store name
          setForm((prev) => ({ ...prev, manufacturer: name }));
        }
      } catch (err) {
        console.error("Error fetching store data for lot form:", err);
      }
    };
    fetchStoreData();
  }, [storeId]);

  // Fetch this store's products for the Product Name dropdown
  useEffect(() => {
    if (!storeId) return;
    const fetchStoreProducts = async () => {
      try {
        setIsLoadingProducts(true);
        const response = await getMyProduct({
          store_id: storeId,
          limit: 200,
          pageNumber: 1,
        });
        const raw = response?.data?.products || response?.data?.data || [];
        setStoreProducts(Array.isArray(raw) ? raw : []);
      } catch (err) {
        console.error("Error fetching store products for lot form:", err);
        setStoreProducts([]);
      } finally {
        setIsLoadingProducts(false);
      }
    };
    fetchStoreProducts();
  }, [storeId]);

  const [documents, setDocuments] = useState({
    quality: [],
    regulatory: [],
  });

  // Simulated system company data
  const companyData = {
    name: "Custodiy Financial Services Ltd",
    address: "Toronto, Canada",
    registration: "MSB FINTRAC Registered",
  };

  const requiredComplete = useMemo(() => {
    return (
      !!form.lotNumber &&
      !!form.productName &&
      !!form.productionDate &&
      !!form.expiryDate 
    );
  }, [form, documents]);

  const completion = useMemo(() => {
    // Completion is now based only on core fields (no document requirement)
    const total = 4;
    let score = 0;
    if (form.lotNumber) score++;
    if (form.productName) score++;
    if (form.productionDate) score++;
    if (form.expiryDate) score++;
    return Math.round((score / total) * 100);
  }, [form]);

  const toggle = (key) => {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updated = { ...form, [name]: value };

    if (name === "productCategory") {
      const ranges = {
        "Room Controlled": { min: "15", max: "25" },
        Refrigerated: { min: "2", max: "8" },
        Frozen: { min: "-25", max: "-15" },
        "Deep Frozen": { min: "-80", max: "-60" },
      };

      if (ranges[value]) {
        updated.temperatureMin = ranges[value].min;
        updated.temperatureMax = ranges[value].max;
      }
    }

    setForm(updated);
  };

  const addDocument = (category, name) => {
    setDocuments((prev) => {
      if (prev[category].includes(name)) return prev;
      return {
        ...prev,
        [category]: [...prev[category], name],
      };
    });
  };

  const removeDocument = (category, index) => {
    setDocuments((prev) => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index),
    }));
  };

  const handleFiles = (files, category = "quality") => {
    const fileArray = Array.from(files || []);
    if (!fileArray.length) return;

    setDocuments((prev) => ({
      ...prev,
      [category]: [...prev[category], ...fileArray],
    }));
  };

  // ================= CERTIFICATION ENGINE =================

  const [certificateNumber, setCertificateNumber] = useState(null);
  const [pdfHash, setPdfHash] = useState("");

  const generateCertificateNumber = () => {
    const year = new Date().getFullYear();
    const progressive = Math.floor(Math.random() * 900000 + 100000);
    return `CTY-${year}-${progressive}`;
  };

  const sha256 = async (text) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  };

  const buildPdfHtml = (certNo, hash) => {
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${hash}`;

    return `
    <html>
    <head>
      <style>
        body { font-family: "Times New Roman", serif; padding:60px; line-height:1.6; }
        h1 { text-align:center; margin-bottom:40px;color:black }
        h2 { margin-top:40px; font-size:16px;color:black }
        .article { margin-top:20px; }
        .footer { margin-top:60px; font-size:12px; border-top:1px solid #000; padding-top:20px; }
        .signature { margin-top:80px; border-top:1px solid #000; width:300px; }
        .meta { font-size:13px; }
      </style>
    </head>
    <body>
      <h1>LOT CERTIFICATION ACT</h1>
      <div class="meta">
        Certificate No: ${certNo}<br/>
        Issued by: ${companyData.name}<br/>
        Registration: ${companyData.registration}<br/>
        Address: ${companyData.address}
      </div>

      <h2>Article 1 — Identification of the Batch</h2>
      <div class="article">
        Lot Number: ${form.lotNumber}<br/>
        Product Name: ${form.productName}<br/>
        Category: ${form.productCategory}<br/>
        GTIN: ${form.gtin}
      </div>

      <h2>Article 2 — Production Data</h2>
      <div class="article">
        Manufacturer: ${form.manufacturer}<br/>
        Production Date: ${form.productionDate}<br/>
        Expiry Date: ${form.expiryDate}<br/>
        Quantity: ${form.quantity || "N/A"}<br/>
        Price: ${form.price || "N/A"}
      </div>

      <h2>Article 3 — Compliance & Storage</h2>
      <div class="article">
        Temperature Range: ${form.temperatureMin}°C — ${form.temperatureMax}°C
      </div>

      <h2>Article 4 — Documentation</h2>
      <div class="article">
        Quality Documents: ${documents.quality.join(", ") || "None"}<br/>
        Regulatory Documents: ${documents.regulatory.join(", ") || "None"}
      </div>

      <h2>Article 5 — Digital Integrity</h2>
      <div class="article">
        SHA256 Hash: ${hash}
      </div>

      <div style="margin-top:40px; width:200px; height: 100%;">
        <img src="${qrUrl}" />
      </div>

      <div class="signature">
        Authorized Digital Signature
      </div>

      <div class="footer">
        Generated on ${new Date().toLocaleString()}<br/>
        This document constitutes a digitally generated certification compliant with GMP traceability standards.
      </div>
    </body>
    </html>
    `;
  };

  const generateLotPdf = async () => {
    const certNo = generateCertificateNumber();
    const rawContent = JSON.stringify({ form, documents, certNo });
    const hash = await sha256(rawContent);

    setCertificateNumber(certNo);
    setPdfHash(hash);
    setShowPdfPreview(true);
  };

  const confirmDownloadPdf = () => {
    const html = buildPdfHtml(certificateNumber, pdfHash);
    const newWindow = window.open("", "_blank");
    newWindow.document.write(html);
    newWindow.document.close();
    newWindow.focus();
    newWindow.print();
    setShowPdfPreview(false);

    setDocuments((prev) => ({
      ...prev,
      regulatory: [...prev.regulatory, `Lot_${form.lotNumber}_Certification.pdf`],
    }));
  };

  const buildLotPayload = () => {
    // Find a matching product by ID (fallback to first product)
    const matchedProduct =
      storeProducts?.find(
        (p) => String(p._id) === String(selectedProductId)
      ) || storeProducts?.[0];

    const product_id = matchedProduct?._id;
    const category_id =
      form.productCategoryId ||
      matchedProduct?.category_id ||
      matchedProduct?.category?._id ||
      matchedProduct?.category_name?._id ||
      matchedProduct?.subcategory_id ||
      matchedProduct?.category_detail?.sub_categories?.[0]?._id ||
      matchedProduct?.category_detail?.sub_categories?._id ||
      null;

    const compliance = [
      {
        minimum_validated_storage_temperature:
          form.temperatureMin !== ""
            ? Number(form.temperatureMin)
            : undefined,
        maximum_validated_storage_temperature:
          form.temperatureMax !== ""
            ? Number(form.temperatureMax)
            : undefined,
      },
    ];

    const production = [
      {
        manufacturing_batch_number: form.manufacturer,
        production_date: form.productionDate
          ? new Date(form.productionDate).toISOString()
          : undefined,
        expiry_date: form.expiryDate
          ? new Date(form.expiryDate).toISOString()
          : undefined,
        // quantity:
        //   form.quantity !== "" && form.quantity !== null
        //     ? Number(form.quantity)
        //     : undefined,
        // price:
        //   form.price !== "" && form.price !== null
        //     ? Number(form.price)
        //     : undefined,
      },
    ];

    return {
      lot_number: form.lotNumber,
      product_id,
      category_id,
      store_id: storeId,
      global_trade_item_number: form.gtin,
      compliance,
      production,
    };
  };

  const handleCreateLot = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);

      // Auto-generate real PDF (using pdf-lib) and attach it as File payload
      const certNo = generateCertificateNumber();
      const rawContent = JSON.stringify({ form, documents, certNo });
      const hash = await sha256(rawContent);

      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([595.28, 841.89]); // A4 size
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const { width, height } = page.getSize();

      const drawText = (text, x, y, size = 12) => {
        page.drawText(text, { x, y, size, font });
      };

      let cursorY = height - 50;
      drawText("Pharmacrowd - Master Lot Certification", 50, cursorY, 16);
      cursorY -= 30;
      drawText(`Certificate No: ${certNo}`, 50, cursorY);
      cursorY -= 20;
      drawText(`Lot Number: ${form.lotNumber || "-"}`, 50, cursorY);
      cursorY -= 20;
      drawText(`Store ID: ${storeId}`, 50, cursorY);
      cursorY -= 20;
      drawText(`GTIN: ${form.gtin || "-"}`, 50, cursorY);
      cursorY -= 30;
      drawText("Integrity Hash (sha256 of lot payload):", 50, cursorY);
      cursorY -= 16;
      drawText(hash, 50, cursorY, 8);

      const pdfBytes = await pdfDoc.save();
      const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
      const pdfFile = new File(
        [pdfBlob],
        `Lot_${form.lotNumber || "Certification"}.pdf`,
        { type: "application/pdf" }
      );

      const payload = buildLotPayload();
      const fd = new FormData();

      // Append auto-generated PDF file
      fd.append("File", pdfFile);

      // Append structured fields
      fd.append("lot_number", payload.lot_number || "");
      if (payload.product_id) fd.append("product_id", payload.product_id);
      if (payload.category_id) fd.append("category_id", payload.category_id);
      if (payload.store_id) fd.append("store_id", payload.store_id);
      fd.append(
        "global_trade_item_number",
        payload.global_trade_item_number || ""
      );
      fd.append("compliance", JSON.stringify(payload.compliance || []));
      fd.append("production", JSON.stringify(payload.production || []));

      const response = await createLot(fd);

      // Show success toast
      if (response?.data?.success) {
        toast.success(response.data.Message || "Lot created successfully!");
        history.push(`/storedetail/${storeId}`);
      } else {
        toast.error(response?.data?.Message || "Failed to create lot. Please try again.");
      }
    } catch (err) {
      console.error("Error creating lot", err);
      // Show error toast
      const errorMessage = err?.response?.data?.Message || err?.response?.data?.message || err?.message || "Failed to create lot. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LayoutWrapper>
      {/* ================= LEFT FORM ================= */}
      <MainArea>
        <HeaderBanner>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
            <button
              type="button"
              onClick={() => history.push(`/storedetail/${storeId}`)}
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "8px",
                padding: "8px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "#5ce9ff",
                fontSize: "14px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(92, 233, 255, 0.2)";
                e.currentTarget.style.borderColor = "rgba(92, 233, 255, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </div>
          <MainTitle>Pharmacrowd  - Master Lot</MainTitle>
          <Subtitle>Lot Creation Engine</Subtitle>
        </HeaderBanner>

        <Section
          title="General"
          openKey="general"
          sections={sections}
          toggle={toggle}
        >
          <Field
            label="Lot Number"
            help="Unique batch identifier assigned by the manufacturer."
            name="lotNumber"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
          />
          <div style={{ marginBottom: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Label>Product Name</Label>
            </div>
            <ProductSelectWrapper style={{ marginTop: 6 }}>
              {(() => {
                const selected =
                  storeProducts.find(
                    (p) => String(p._id) === String(selectedProductId)
                  ) || null;
                const thumb =
                  selected?.product_image ||
                  selected?.pictures?.[0] ||
                  selected?.image ||
                  selected?.coverImage ||
                  "";
                return (
                  <>
                    <ProductSelectDisplay
                      type="button"
                      onClick={() =>
                        !isLoadingProducts &&
                        setProductDropdownOpen((open) => !open)
                      }
                    >
                      <ProductSelectLeft>
                        {thumb ? (
                          <ProductThumb
                            style={{
                              backgroundImage: `url(${thumb})`,
                            }}
                          />
                        ) : (
                          <ProductThumb />
                        )}
                        <ProductSelectName>
                          {isLoadingProducts
                            ? "Loading products..."
                            : selected
                            ? selected.title || selected.name || "Untitled"
                            : "Select product"}
                        </ProductSelectName>
                      </ProductSelectLeft>
                      <span style={{ fontSize: 10, color: "#64748b" }}>
                        {productDropdownOpen ? "▲" : "▼"}
                      </span>
                    </ProductSelectDisplay>
                    {productDropdownOpen && storeProducts.length > 0 && (
                      <ProductSelectDropdown>
                        {storeProducts.map((p) => {
                          const img =
                            p.product_image ||
                            p.pictures?.[0] ||
                            p.image ||
                            p.coverImage ||
                            "";
                          return (
                            <ProductOption
                              key={p._id}
                              type="button"
                              onClick={() => {
                                setSelectedProductId(p._id);
                                // Extract subcategory name from category_detail[0].sub_categories[0].sub_category_name
                                const subcategoryName = 
                                  p.category_detail?.[0]?.sub_categories?.[0]?.sub_category_name ||
                                  "";
                                const subcategoryId = 
                                  p.subcategory_id ||
                                  p.category_detail?.[0]?.sub_categories?.[0]?._id ||
                                  "";
                                
                                setForm((prev) => ({
                                  ...prev,
                                  productName: p.title || p.name || "",
                                  gtin:
                                    p.global_trade_item_number ||
                                    p.gtin ||
                                    prev.gtin,
                                  productCategory: subcategoryName || "",
                                  productCategoryId: subcategoryId || prev.productCategoryId,
                                }));
                                setProductDropdownOpen(false);
                              }}
                            >
                              {img && (
                                <ProductThumb
                                  style={{
                                    marginRight: 10,
                                    backgroundImage: `url(${img})`,
                                  }}
                                />
                              )}
                              <ProductSelectName>
                                {p.title || p.name || "Untitled"}
                              </ProductSelectName>
                            </ProductOption>
                          );
                        })}
                      </ProductSelectDropdown>
                    )}
                  </>
                );
              })()}
            </ProductSelectWrapper>
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                color: "#64748b",
              }}
            >
              Choose one of your store products. GTIN and category will be
              auto-filled when available.
            </div>
          </div>

          <div style={{ marginBottom: 22 }}>
            <Label>Product Category</Label>
            <select
              name="productCategory"
              value={form.productCategory}
              onChange={handleChange}
              disabled
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 10,
                background: "#0d1220",
                color: "#fff",
                marginTop: 6,
              }}
            >
              <option value="">
                {form.productCategory
                  ? form.productCategory
                  : "Category will be filled from selected product"}
              </option>
            </select>
            <div
              style={{
                fontSize: 11,
                color: "#94a3b8",
                marginTop: 4,
              }}
            >
              Selecting a category auto-fills recommended storage range.
            </div>
          </div>

          <Field
            label="GTIN"
            help="Global Trade Item Number used for product identification."
            name="gtin"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
          />
        </Section>

        <Section
          title="Production"
          openKey="production"
          sections={sections}
          toggle={toggle}
        >
          <Field
            label="Manufacturer"
            help="Legal entity responsible for manufacturing the batch."
            name="manufacturer"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
            disabled={true}
          />
          <Field
            label="Production Date"
            help="Date on which the batch was produced."
            name="productionDate"
            type="date"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
          />
          <Field
            label="Expiry Date"
            help="Date after which the product must not be used."
            name="expiryDate"
            type="date"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
          />
          {/* <Field
            label="Quantity"
            help="Total quantity in this manufacturing batch."
            name="quantity"
            type="number"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
          />
          <Field
            label="Price"
            help="Unit price for this lot (optional)."
            name="price"
            type="number"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
          /> */}
        </Section>

        <Section
          title="Compliance"
          openKey="compliance"
          sections={sections}
          toggle={toggle}
        >
          <Field
            label="Temp Min"
            help="Minimum validated storage temperature in °C."
            name="temperatureMin"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
          />
          <Field
            label="Temp Max"
            help="Maximum validated storage temperature in °C."
            name="temperatureMax"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
          />
        </Section>

        {/* Documents upload section disabled: external PDFs are not allowed.
            Lot documentation is provided by the generated summary PDF instead. */}
        {false && (
          <Section
            title="Documents"
            openKey="documents"
            sections={sections}
            toggle={toggle}
          >
            {/* Document upload UI removed */}
          </Section>
        )}

        {/* 🔐 NOTARIZATION BUTTON */}
        <div style={{ display: "flex", gap: "12px", marginTop: 30 }}>
          <button
            type="button"
            onClick={() => history.push(`/storedetail/${storeId}`)}
            style={{
              padding: "14px 26px",
              borderRadius: 14,
              background: "rgba(255, 255, 255, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)";
              e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
              e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
            }}
          >
            Close
          </button>
          <button
            disabled={!requiredComplete || isSubmitting}
            onClick={handleCreateLot}
            style={{
              padding: "14px 26px",
              borderRadius: 14,
              background:
                requiredComplete && !isSubmitting
                  ? "linear-gradient(90deg,#00f7d2,#5ce9ff)"
                  : "rgba(255,255,255,0.08)",
              border:
                requiredComplete && !isSubmitting
                  ? "none"
                  : "1px solid rgba(255,255,255,0.1)",
              color:
                requiredComplete && !isSubmitting ? "#000" : "#64748b",
              fontWeight: 600,
              cursor:
                requiredComplete && !isSubmitting
                  ? "pointer"
                  : "not-allowed",
              transition: "all 0.3s ease",
            }}
          >
            {isSubmitting ? "Saving..." : "Add Lot"}
          </button>
        </div>
      </MainArea>

      {/* ================= RIGHT MAIN SIDEBAR (DATA) ================= */}
      <Sidebar>
        <PreviewSection>
          <SectionHeader>Lot Data Preview</SectionHeader>
          <DataRow>
            <span>Lot</span>
            <strong>{form.lotNumber || "—"}</strong>
          </DataRow>
          <DataRow>
            <span>Product</span>
            <strong>{form.productName || "—"}</strong>
          </DataRow>
          <DataRow>
            <span>Manufacturer</span>
            <strong>{form.manufacturer || "—"}</strong>
          </DataRow>
          <DataRow>
            <span>Documents</span>
            <strong>
              {documents.quality.length + documents.regulatory.length}
            </strong>
          </DataRow>
        </PreviewSection>

        <ProgressWrapper>
          <ProgressFill style={{ width: `${completion}%` }} />
        </ProgressWrapper>
        <div style={{ marginTop: 8, color: "#94a3b8" }}>
          {completion}% Complete
        </div>

        <HierarchyButton
          style={{ width: "100%", marginTop: 20 }}
          onClick={() => setShowHierarchy(true)}
        >
          Open Supply Chain Hierarchy
        </HierarchyButton>

        <HierarchyButton
          style={{
            width: "100%",
            marginTop: 12,
            background: "linear-gradient(90deg,#00f7d2,#5ce9ff)",
            color: "#000",
            border: "none",
          }}
          onClick={generateLotPdf}
        >
          Generate Lot Summary PDF
        </HierarchyButton>
      </Sidebar>

      {/* ================= SLIDE HIERARCHY SIDEBAR ================= */}
      {showHierarchy && (
        <Overlay onClick={() => setShowHierarchy(false)}>
          <SlidePanel onClick={(e) => e.stopPropagation()}>
            <SlideHeader>
              <div style={{ fontWeight: 600 }}>
                Supply Chain Hierarchy
              </div>
              <div
                style={{ cursor: "pointer" }}
                onClick={() => setShowHierarchy(false)}
              >
                ✕
              </div>
            </SlideHeader>
            <div style={{ marginTop: 30 }}>
              <HierarchyTree form={form} />
            </div>
          </SlidePanel>
        </Overlay>
      )}

      {/* ================= PDF PREVIEW MODAL ================= */}
      {showPdfPreview && certificateNumber && pdfHash && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <div
            style={{
              width: "70%",
              height: "80%",
              background: "#fff",
              color: "#000",
              padding: 30,
              overflowY: "auto",
              borderRadius: 10,
            }}
          >
            <div
              dangerouslySetInnerHTML={{ __html: buildPdfHtml(certificateNumber, pdfHash) }}
            />
            <div style={{ marginTop: 20, textAlign: "right" }}>
              <button
                type="button"
                onClick={() => setShowPdfPreview(false)}
                style={{ marginRight: 10 }}
              >
                Cancel
              </button>
              <button type="button" onClick={confirmDownloadPdf}>
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {isSubmitting && (
        <LotLoadingOverlay>
          <Ring size={65} color="#5ce9ff" />
        </LotLoadingOverlay>
      )}
    </LayoutWrapper>
  );
}

// ================= COMPONENTS =================

const Section = ({ title, openKey, children, sections, toggle }) => (
  <Card>
    <CardHeader onClick={() => toggle(openKey)}>
      <SectionTitle>{title}</SectionTitle>
      <span style={{ color: "#00f7d2" }}>
        {sections[openKey] ? "−" : "+"}
      </span>
    </CardHeader>
    {sections[openKey] && <div style={{ marginTop: 20 }}>{children}</div>}
  </Card>
);

const Field = ({
  label,
  help,
  name,
  type = "text",
  form,
  activeField,
  setActiveField,
  handleChange,
  disabled = false,
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const value = form[name];

  const getSuggestion = () => {
    if (!value) return null;

    if (name === "expiryDate" && form.productionDate) {
      if (new Date(value) <= new Date(form.productionDate)) {
        return "Expiry date should be later than production date.";
      }
    }

    if (name === "temperatureMin" && form.temperatureMax) {
      if (Number(value) >= Number(form.temperatureMax)) {
        return "Min temperature should be lower than Max temperature.";
      }
    }

    if (name === "temperatureMax" && form.temperatureMin) {
      if (Number(value) <= Number(form.temperatureMin)) {
        return "Max temperature should be higher than Min temperature.";
      }
    }

    return null;
  };

  const suggestion = getSuggestion();

  return (
    <div style={{ marginBottom: 22, position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Label>{label}</Label>
        {help && (
          <span
            onClick={() => setShowHelp(!showHelp)}
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: showHelp
                ? "#00f7d2"
                : "rgba(0,247,210,0.2)",
              color: showHelp ? "#000" : "#00f7d2",
              fontSize: 11,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            i
          </span>
        )}
      </div>

      {showHelp && help && (
        <div
          style={{
            marginTop: 8,
            background: "#0f172a",
            border: "1px solid rgba(0,247,210,0.3)",
            padding: "8px 10px",
            borderRadius: 8,
            fontSize: 11,
            color: "#cbd5e1",
            boxShadow: "0 0 15px rgba(0,247,210,0.2)",
          }}
        >
          {help}
        </div>
      )}

      <TextInput
        type={type}
        name={name}
        value={value}
        disabled={disabled}
        onFocus={() => setActiveField(name)}
        onBlur={() => setActiveField(null)}
        onChange={handleChange}
        style={{
          marginTop: 6,
          border: suggestion
            ? "1px solid #ff6b6b"
            : activeField === name
            ? "1px solid #00f7d2"
            : undefined,
          boxShadow: suggestion
            ? "0 0 10px rgba(255,107,107,0.5)"
            : activeField === name
            ? "0 0 10px rgba(0,247,210,0.6)"
            : "none",
        }}
      />

      {suggestion && (
        <div
          style={{
            marginTop: 6,
            fontSize: 11,
            color: "#ff6b6b",
          }}
        >
          {suggestion}
        </div>
      )}
    </div>
  );
};

const HierarchyTree = ({ form }) => {
  const generateHash = (val) => {
    try {
      return btoa(val).substring(0, 10);
    } catch {
      return "—";
    }
  };

  const rootQty = 0;

  const treeData = {
    id: "root",
    label: "MasterLot",
    lot: form.lotNumber || "N/A",
    qty: rootQty,
    status: "Minted",
    hash: generateHash(form.lotNumber + rootQty),
    children: [
      {
        id: "A",
        label: "Distributor A",
        lot: form.lotNumber ? form.lotNumber + "-A" : "N/A",
        qty: Math.floor(rootQty / 2),
        status: "Transferred",
        hash: generateHash("A" + form.lotNumber),
        children: [
          {
            id: "A1",
            label: "Pharmacy A1",
            lot: form.lotNumber ? form.lotNumber + "-A1" : "N/A",
            qty: Math.floor(rootQty / 4),
            status: "Open",
            hash: generateHash("A1" + form.lotNumber),
          },
          {
            id: "A2",
            label: "Pharmacy A2",
            lot: form.lotNumber ? form.lotNumber + "-A2" : "N/A",
            qty: Math.floor(rootQty / 4),
            status: "Open",
            hash: generateHash("A2" + form.lotNumber),
          },
        ],
      },
      {
        id: "B",
        label: "Distributor B",
        lot: form.lotNumber ? form.lotNumber + "-B" : "N/A",
        qty: Math.floor(rootQty / 2),
        status: "Transferred",
        hash: generateHash("B" + form.lotNumber),
        children: [
          {
            id: "B1",
            label: "Pharmacy B1",
            lot: form.lotNumber ? form.lotNumber + "-B1" : "N/A",
            qty: Math.floor(rootQty / 2),
            status: "Open",
            hash: generateHash("B1" + form.lotNumber),
          },
        ],
      },
    ],
  };

  const Node = ({ node, level = 0 }) => (
    <div
      style={{
        marginLeft: level * 20,
        marginTop: 20,
        position: "relative",
      }}
    >
      {level !== 0 && (
        <svg
          width="20"
          height="20"
          style={{
            position: "absolute",
            left: -20,
            top: 10,
          }}
        >
          <line
            x1="20"
            y1="0"
            x2="0"
            y2="10"
            stroke="#00f7d2"
            strokeWidth="1.5"
          />
        </svg>
      )}

      <div
        style={{
          padding: "12px 16px",
          borderRadius: 12,
          background: "#111c2a",
          border: "1px solid rgba(0,247,210,0.4)",
          boxShadow: "0 0 12px rgba(0,247,210,0.3)",
          transition: "all 0.4s ease",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 13 }}>{node.label}</div>
        <div style={{ fontSize: 11, color: "#5ce9ff" }}>
          Lot: {node.lot}
        </div>
        <div style={{ fontSize: 11 }}>Qty: {node.qty}</div>
        <div style={{ fontSize: 10, color: "#94a3b8" }}>
          Hash: {node.hash}
        </div>
        <div
          style={{
            marginTop: 6,
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: 8,
            fontSize: 10,
            background:
              node.status === "Minted"
                ? "rgba(0,247,210,0.2)"
                : node.status === "Transferred"
                ? "rgba(92,233,255,0.2)"
                : "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          {node.status}
        </div>
      </div>

      {node.children &&
        node.children.map((child) => (
          <Node key={child.id} node={child} level={level + 1} />
        ))}
    </div>
  );

  return (
    <div style={{ animation: "slideIn 0.4s ease" }}>
      <Node node={treeData} />
    </div>
  );
};

